/**
 * Health Check Service
 * Monitors API health and provides fallback strategies
 */

import { buildApiUrl } from "../../config/api-config";
import type { HttpClient } from "../client";
import type { HealthCheckResponse } from "../types";

export interface ServiceHealth {
	name: string;
	status: "healthy" | "degraded" | "unhealthy";
	responseTime: number;
	lastCheck: string;
	endpoint: string;
}

export class HealthService {
	private httpClient: HttpClient;
	private healthCache = new Map<
		string,
		{ health: ServiceHealth; expiry: number }
	>();
	private readonly CACHE_TTL = 30000; // 30 seconds

	constructor(httpClient: HttpClient) {
		this.httpClient = httpClient;
	}

	// ==================== Health Check Methods ====================

	async checkApiHealth(): Promise<HealthCheckResponse> {
		const startTime = Date.now();

		try {
			const url = buildApiUrl("gateway", "/health");
			const response = await this.httpClient.get<HealthCheckResponse>(url, {
				timeout: 5000, // Short timeout for health checks
			});

			return {
				...response,
				responseTime: Date.now() - startTime,
			} as HealthCheckResponse & { responseTime: number };
		} catch (_error) {
			return {
				status: "unhealthy",
				service: "api-gateway",
				environment: "unknown",
				timestamp: new Date().toISOString(),
				checks: {
					database: false,
					cache: false,
					storage: false,
				},
				responseTime: Date.now() - startTime,
			} as HealthCheckResponse & { responseTime: number };
		}
	}

	async checkServiceHealth(serviceName: string): Promise<ServiceHealth> {
		// Check cache first
		const cached = this.getFromCache(serviceName);
		if (cached) {
			return cached;
		}

		const startTime = Date.now();
		const endpoint = this.getServiceHealthEndpoint(serviceName);

		try {
			await this.httpClient.get(endpoint, {
				timeout: 3000,
			});

			const health: ServiceHealth = {
				name: serviceName,
				status: "healthy",
				responseTime: Date.now() - startTime,
				lastCheck: new Date().toISOString(),
				endpoint,
			};

			this.setCache(serviceName, health);
			return health;
		} catch (_error) {
			const health: ServiceHealth = {
				name: serviceName,
				status: "unhealthy",
				responseTime: Date.now() - startTime,
				lastCheck: new Date().toISOString(),
				endpoint,
			};

			this.setCache(serviceName, health);
			return health;
		}
	}

	async checkAllServicesHealth(): Promise<ServiceHealth[]> {
		const services = ["auth", "tenant", "packages", "analytics", "storage"];

		const healthChecks = services.map((service) =>
			this.checkServiceHealth(service).catch((error) => ({
				name: service,
				status: "unhealthy" as const,
				responseTime: 0,
				lastCheck: new Date().toISOString(),
				endpoint: this.getServiceHealthEndpoint(service),
				error: error.message,
			})),
		);

		return Promise.all(healthChecks);
	}

	// ==================== Fallback Strategy ====================

	async isApiAvailable(): Promise<boolean> {
		try {
			const health = await this.checkApiHealth();
			return health.status === "healthy" || health.status === "degraded";
		} catch {
			return false;
		}
	}

	async getRecommendedMode(): Promise<"api" | "demo"> {
		const isAvailable = await this.isApiAvailable();
		return isAvailable ? "api" : "demo";
	}

	async shouldUseFallback(): Promise<boolean> {
		const health = await this.checkApiHealth();

		// Use fallback if unhealthy or response time > 5 seconds
		return health.status === "unhealthy" || (health as any).responseTime > 5000;
	}

	// ==================== Monitoring ====================

	startHealthMonitoring(interval: number = 60000): () => void {
		const intervalId = setInterval(async () => {
			try {
				const health = await this.checkApiHealth();

				// Log health status changes
				if (health.status !== "healthy") {
					console.warn(`API health status: ${health.status}`, health);
				}

				// Emit custom event for health status
				if (typeof window !== "undefined") {
					window.dispatchEvent(
						new CustomEvent("api-health-update", {
							detail: health,
						}),
					);
				}
			} catch (error) {
				console.error("Health monitoring error:", error);
			}
		}, interval);

		return () => clearInterval(intervalId);
	}

	// ==================== Utilities ====================

	private getServiceHealthEndpoint(serviceName: string): string {
		switch (serviceName) {
			case "auth":
				return buildApiUrl("auth", "/health");
			case "tenant":
				return buildApiUrl("tenant", "/health");
			case "packages":
				return buildApiUrl("packages", "/health");
			case "analytics":
				return buildApiUrl("analytics", "/health");
			case "storage":
				return buildApiUrl("storage", "/health");
			default:
				return buildApiUrl("gateway", "/health");
		}
	}

	private getFromCache(serviceName: string): ServiceHealth | null {
		const cached = this.healthCache.get(serviceName);

		if (!cached) {
			return null;
		}

		if (Date.now() > cached.expiry) {
			this.healthCache.delete(serviceName);
			return null;
		}

		return cached.health;
	}

	private setCache(serviceName: string, health: ServiceHealth): void {
		const expiry = Date.now() + this.CACHE_TTL;
		this.healthCache.set(serviceName, { health, expiry });
	}

	clearCache(): void {
		this.healthCache.clear();
	}
}
